/**
 * YourFeeHereTile — personalised fee-status tile for a single country.
 *
 * Drop it onto any uni/degree detail page and it will:
 *   1. Call `/api/fee-status/me` once (or accept a pre-fetched map).
 *   2. Resolve the country-name string on the parent page to the engine
 *      key (UK, US, DE, …).
 *   3. Render a card showing the inferred status, a short reason, and
 *      a one-line "your fee here" line that swaps the home / intl rate
 *      from the country-level catalogue (`HOME_FEE_FALLBACK_USD` and
 *      the parent-passed intlFeeUsd).
 *   4. Fall back cleanly to "Showing international rate — sign in to
 *      personalise" when the viewer is logged out.
 *
 * It intentionally doesn't fetch the whole degree-cost block — the host
 * page already has that — so this stays a small, self-contained chip.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { engineKeyFromCountry, HOME_FEE_FALLBACK_USD } from "@/lib/countries";

export default function YourFeeHereTile({
  country,                 // e.g. "United Kingdom", "USA", "Germany"
  intlFeeUsd,              // optional — host passes the uni's intl rate
  homeFeeUsd,              // optional — host passes the uni's domestic rate
  compact = false,
  testId = "your-fee-here-tile",
  indicative = false,      // intl fee is a university-wide, proxy or undated figure
  note = null,             // plain-language basis/year qualifier for the intl fee
  homeIndicative = false,  // same, decided separately for the home fee
  homeNote = null,
}) {
  const { format } = useCurrency();
  const [inference, setInference] = useState(null);
  const [state, setState]         = useState("loading");   // loading | loggedIn | loggedOut | unknown

  const engineKey = engineKeyFromCountry(country);

  useEffect(() => {
    if (!engineKey) {
      setState("unknown");
      return;
    }
    api.get("/fee-status/me")
      .then((r) => {
        const map = r.data?.by_country || {};
        const inf = map[engineKey];
        setInference(inf || null);
        setState(inf ? "loggedIn" : "loggedOut");
      })
      .catch(() => setState("loggedOut"));
  }, [engineKey]);

  // Compute the final per-year number using the same precedence the
  // Compare page uses: course rate (passed in) → country fallback.
  const isHome = inference?.status === "home";
  const homeIsCountryEstimate = isHome && !homeFeeUsd && !!HOME_FEE_FALLBACK_USD[engineKey];
  const perYear = isHome
    ? (homeFeeUsd || HOME_FEE_FALLBACK_USD[engineKey] || null)
    : (intlFeeUsd || null);
  const showIndicative = isHome ? (homeIsCountryEstimate || homeIndicative) : indicative;
  const shownNote = isHome ? (homeIsCountryEstimate ? "Country-level estimate, not this university's published fee" : homeNote) : note;

  // Unknown country — don't render; it would be noise.
  if (state === "unknown") return null;
  if (state === "loading") {
    return (
      <div
        data-testid={`${testId}-loading`}
        className="rounded-xl border border-stone-200 bg-white px-4 py-3 text-[12px] text-stone-500"
      >
        Loading your fee status…
      </div>
    );
  }

  const badgeClass = isHome
    ? "bg-emerald-100 text-emerald-800"
    : "bg-stone-100 text-stone-700";
  const borderColor = isHome ? "border-emerald-200 bg-emerald-50/40" : "border-stone-200 bg-white";

  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${borderColor} px-4 py-3.5 space-y-2`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {isHome
            ? <ShieldCheck className="w-4 h-4 text-emerald-700" />
            : <AlertTriangle className="w-4 h-4 text-stone-500" />}
          <div className="font-underground-sc tracking-wider text-[11px] text-stone-600">
            Your fee here
          </div>
        </div>
        <span
          data-testid={`${testId}-badge`}
          className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full ${badgeClass}`}
        >
          {isHome ? "Home fee" : "Intl fee"}
        </span>
      </div>

      <div className="text-stone-900 font-editorial text-xl tabular-nums" data-testid={`${testId}-amount`}>
        {perYear ? (showIndicative ? `≈ ${format(perYear)} / yr (indicative)` : `${format(perYear)} / yr`) : "Not yet verified"}
      </div>
      {perYear && shownNote && (
        <div className="text-[11.5px] text-stone-500 leading-snug" data-testid={`${testId}-note`}>{shownNote}</div>
      )}

      {inference?.reason && !compact && (
        <div className="text-[11.5px] text-stone-600 leading-snug">
          {inference.reason}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1 text-[11px]">
        {state === "loggedOut" && (
          <Link
            to="/profile"
            className="text-[#4C1D95] hover:underline font-semibold"
            data-testid={`${testId}-signin`}
          >
            Sign in to personalise
          </Link>
        )}
        {inference?.authority_url && (
          <a
            href={inference.authority_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
            data-testid={`${testId}-authority`}
          >
            Confirm with authority <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}
